import { Button } from "@/components/ui/button";
import { ArrowRight, Lightbulb, Target, Users, Zap, CheckCircle2, BarChart3, CreditCard, Mail, Rocket, Star, TrendingUp } from "lucide-react";
import { SEO } from "@/components/SEO";

const Guides = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Guides & Documentation - Test Your Product Ideas Without Code | WillTheyConvert"
        description="Learn how WillTheyConvert helps founders, product managers, and entrepreneurs validate their startup ideas without writing a single line of code."
        keywords="startup validation process, how to validate startup ideas, product testing without code, startup idea validation, fake door testing, implementation guides"
        canonicalUrl="/guides"
        ogTitle="Complete Guide to Product Validation & Implementation"
        ogDescription="See how founders, product managers, and indie makers use our platform to test demand for their ideas before investing in development."
        ogImage="/images/og-image.jpg"
      />

      {/* Hero Section */}
      <div className="px-4 py-16 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="text-center">
          <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl md:text-6xl">
            Guides & Documentation
          </h1>
          <p className="max-w-2xl mx-auto mt-6 text-xl text-gray-500">
            Everything you need to know about validating your ideas and implementing our tools
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 py-16 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          {/* What is Fake Door Testing Section */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">What is Fake Door Testing?</h2>
            <div className="prose prose-lg text-gray-600 mb-8">
              <p>
                Fake door testing is a powerful validation method used by successful startups to test market demand before building a product. Instead of spending months developing features that might not resonate with users, you create a simulated version of your product to measure real user interest.
              </p>
              <p>
                This approach has been used by some of the most successful companies in tech:
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-blue-50 p-6 rounded-xl">
                <h3 className="font-semibold text-blue-900 mb-2">Airbnb</h3>
                <p className="text-blue-800">Used fake door testing to validate new property types and experiences, ensuring market demand before expanding their platform.</p>
              </div>
              <div className="bg-purple-50 p-6 rounded-xl">
                <h3 className="font-semibold text-purple-900 mb-2">Dropbox</h3>
                <p className="text-purple-800">Validated their initial concept with a landing page and demo video, gathering 70,000+ signups before building.</p>
              </div>
              <div className="bg-orange-50 p-6 rounded-xl">
                <h3 className="font-semibold text-orange-900 mb-2">Product Hunt</h3>
                <p className="text-orange-800">Tested new features like Collections using fake doors to gauge user interest before development.</p>
              </div>
              <div className="bg-green-50 p-6 rounded-xl">
                <h3 className="font-semibold text-green-900 mb-2">Groupon</h3>
                <p className="text-green-800">Started with a WordPress blog and manual deal fulfillment to validate their business model.</p>
              </div>
            </div>
          </section>

          {/* Who is it for section */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Who is it for?</h2>
            <div className="grid gap-8">
              <div className="flex items-start gap-4">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Target className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Startup Founders</h3>
                  <p className="text-gray-600">
                    Perfect for entrepreneurs who want to validate their startup ideas before investing time and money into development. Test market demand, pricing strategies, and feature ideas with real users.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Users className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Product Managers</h3>
                  <p className="text-gray-600">
                    Ideal for product teams who need to validate new features or products. Get real user feedback and measure interest before committing development resources.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Lightbulb className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Indie Makers</h3>
                  <p className="text-gray-600">
                    Built for solo developers and indie hackers who want to test their ideas quickly and efficiently. Launch landing pages and measure conversion rates without any coding.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* AI Integration Section */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">AI Agent Compatible</h2>
            <p className="text-gray-600 mb-8">
              Our platform is fully optimized to work with modern AI development environments. Get intelligent assistance in integrating and optimizing your validation flows.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <a
                href="https://lovable.dev"
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <div className="bg-white rounded-xl border p-6 hover:border-blue-200 transition-colors h-full">
                  <div className="w-16 h-16 rounded-lg bg-slate-50 p-2 flex items-center justify-center mb-4">
                    <img
                      src="/images/lovable.webp"
                      alt="Lovable logo"
                      className="w-12 h-12 object-contain"
                    />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Lovable</h3>
                  <p className="text-gray-600">
                    AI development environment for modern web apps. Perfect for integrating validation flows with intelligent assistance.
                  </p>
                </div>
              </a>

              <a
                href="https://replit.com"
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <div className="bg-white rounded-xl border p-6 hover:border-blue-200 transition-colors h-full">
                  <div className="w-16 h-16 rounded-lg bg-slate-50 p-2 flex items-center justify-center mb-4">
                    <img
                      src="/images/replit.webp"
                      alt="Replit logo"
                      className="w-12 h-12 object-contain"
                    />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Replit</h3>
                  <p className="text-gray-600">
                    Cloud development environment with built-in AI capabilities. Seamlessly integrate and test your validation flows.
                  </p>
                </div>
              </a>

              <a
                href="https://bolt.new"
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <div className="bg-white rounded-xl border p-6 hover:border-blue-200 transition-colors h-full">
                  <div className="w-16 h-16 rounded-lg bg-slate-50 p-2 flex items-center justify-center mb-4">
                    <img
                      src="/images/bolt.webp"
                      alt="Bolt logo"
                      className="w-12 h-12 object-contain"
                    />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Bolt</h3>
                  <p className="text-gray-600">
                    AI-powered coding environment. Perfect for web projects and validation flow integration with smart suggestions.
                  </p>
                </div>
              </a>
            </div>
          </section>

          {/* Embed Guide Section */}
          <section className="mb-16" id="how-to-embed">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">How to Embed</h2>
            <p className="text-gray-600 mb-8">
              Follow this simple guide to embed your validation flows seamlessly into your website. Our flexible embedding options work with any website builder or custom code.
            </p>
            <div className="rounded-xl overflow-hidden border border-gray-200 bg-gray-50 mb-8">
              <video 
                className="w-full"
                controls
                poster="/images/full-flow-embed-payment.jpg"
              >
                <source 
                  src="https://res.cloudinary.com/dlotgcnki/video/upload/v1745337783/newvideo_btts6d.mp4" 
                  type="video/mp4" 
                />
                Your browser does not support the video tag.
              </video>
            </div>
          </section>

          {/* Key Features Section */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Platform Features</h2>
            <div className="grid gap-8">
              <div className="flex items-start gap-4">
                <div className="p-2 bg-indigo-100 rounded-lg">
                  <CreditCard className="w-6 h-6 text-indigo-600" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Payment Testing</h3>
                  <p className="text-gray-600">
                    Create realistic payment flows without processing real transactions. Test different price points, subscription models, and purchase flows to find what resonates with your audience.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="p-2 bg-green-100 rounded-lg">
                  <BarChart3 className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Advanced Analytics</h3>
                  <p className="text-gray-600">
                    Get detailed insights into user behavior, including conversion rates, drop-off points, and engagement metrics. Make data-driven decisions about your product development.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <Mail className="w-6 h-6 text-yellow-600" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Lead Collection</h3>
                  <p className="text-gray-600">
                    Build your early adopter list while testing. Capture email addresses of interested users and notify them when your product launches.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="p-2 bg-red-100 rounded-lg">
                  <Rocket className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Quick Setup</h3>
                  <p className="text-gray-600">
                    Launch your test in minutes with our no-code editor. Choose from pre-built templates or customize your flow to match your brand.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* How to Use Section */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">How to Use It</h2>
            <div className="space-y-8">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-semibold">
                  1
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Choose Your Test Type</h3>
                  <p className="text-gray-600">
                    Select from multiple test types including payment testing, newsletter signups, and referral programs. Each type is designed to measure different aspects of user interest.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-semibold">
                  2
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Customize Your Test</h3>
                  <p className="text-gray-600">
                    Use our no-code editor to customize the look and feel of your test. Add your branding, adjust messaging, and set up tracking parameters.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-semibold">
                  3
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Launch & Analyze</h3>
                  <p className="text-gray-600">
                    Share your test with your target audience and track results in real-time. Get detailed analytics on user behavior and conversion rates.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Use Cases Section */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Perfect For Testing</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-100">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">SaaS Products</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-blue-500 mt-0.5" />
                    <span className="text-gray-700">Test different pricing tiers</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-blue-500 mt-0.5" />
                    <span className="text-gray-700">Validate feature sets</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-blue-500 mt-0.5" />
                    <span className="text-gray-700">Measure subscription interest</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-xl border border-purple-100">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Digital Products</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-purple-500 mt-0.5" />
                    <span className="text-gray-700">Online courses and workshops</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-purple-500 mt-0.5" />
                    <span className="text-gray-700">eBooks and digital downloads</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-purple-500 mt-0.5" />
                    <span className="text-gray-700">Premium newsletters</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-teal-50 p-6 rounded-xl border border-green-100">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Marketplaces</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5" />
                    <span className="text-gray-700">Two-sided marketplace demand</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5" />
                    <span className="text-gray-700">Commission structures</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5" />
                    <span className="text-gray-700">Service categories</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-orange-50 to-yellow-50 p-6 rounded-xl border border-orange-100">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Community Products</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-orange-500 mt-0.5" />
                    <span className="text-gray-700">Membership programs</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-orange-500 mt-0.5" />
                    <span className="text-gray-700">Community platforms</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-orange-500 mt-0.5" />
                    <span className="text-gray-700">Social features</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Call to Action */}
          <div className="relative">
            {/* Background decorative elements */}
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute -top-24 -right-24 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl"></div>
              <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
            </div>

            {/* Content */}
            <div className="relative bg-white/50 backdrop-blur-xl rounded-3xl border border-indigo-100 p-12">
              <div className="grid md:grid-cols-2 gap-12 items-center">
                {/* Left Column - Content */}
                <div>
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-500/10 text-indigo-600 font-medium text-sm mb-6">
                    <Star className="w-4 h-4" />
                    Used by Successful Startups
                  </div>
                  <h2 className="text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-blue-600 mb-4">
                    Ready to Validate Your Idea?
                  </h2>
                  <p className="text-lg text-gray-600 mb-8">
                    Join thousands of founders who've used our platform to test their ideas before building. Launch your first test in minutes and get real user feedback.
                  </p>
                  <div className="space-y-4">
                    <Button 
                      className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-4 text-lg bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 rounded-xl" 
                      asChild
                    >
                      <a href="/signup">
                        Start Testing Now <ArrowRight className="w-5 h-5 ml-2" />
                      </a>
                    </Button>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <div className="flex items-center gap-1.5">
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                        14-day free trial
                      </div>
                      <div className="flex items-center gap-1.5">
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                        Free to get started
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column - Image */}
                <div className="relative mx-auto">
                  <div className="relative w-48 h-48">
                    <img 
                      src="/images/willtheyconvertavatar.jpg" 
                      alt="WillTheyConvert Avatar" 
                      className="rounded-2xl shadow-xl object-cover w-full h-full"
                    />
                    {/* Stats overlay */}
                    <div className="absolute -right-24 top-0 bg-white rounded-xl shadow-lg p-4 animate-bounce-slow">
                      <div className="text-sm font-medium text-gray-900">Conversion Rate</div>
                      <div className="text-2xl font-bold text-indigo-600">5.2%</div>
                    </div>
                    <div className="absolute -left-24 bottom-0 bg-white rounded-xl shadow-lg p-4 animate-bounce-slow delay-1000">
                      <div className="text-sm font-medium text-gray-900">Avg. Time to Test</div>
                      <div className="text-2xl font-bold text-indigo-600">2.5 days</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Guides; 